import { z } from "zod";

/**
 * Browser configuration.
 *
 * Separate from `@nwga/config`, which parses the *server's* environment and has
 * no business being importable from the client. Only `VITE_`-prefixed variables
 * exist here — anything else is not in the bundle at all.
 *
 * Two sources, in priority order:
 *
 * 1. **Meta tags in the served `<head>`**, written by whichever process serves
 *    the page (`apps/web/server.ts` under Bun, `apps/web/worker.ts` on
 *    Cloudflare). This is the one that matters in production.
 * 2. **`VITE_*` variables**, baked in at build time. The fallback, and the only
 *    source under `vite dev`, where nothing rewrites the head.
 *
 * Why the meta tags exist at all: a Vite bundle normally freezes its API origin
 * at build time, which means the same artefact cannot be promoted from staging
 * to production — you rebuild, and what you ship is not what you tested. Reading
 * the origin from the document instead makes the bundle environment-agnostic,
 * and makes "point the client at a different API" a variable on the web service
 * rather than a rebuild.
 *
 * Nothing secret goes in a meta tag. A site key identifies a widget; it does not
 * authorise anything, and it is visible in the rendered page either way.
 */

/** Prefixed so these cannot collide with a meta tag that means something to a crawler. */
function metaConfig(name: string): string | undefined {
	if (typeof document === "undefined") return undefined;

	const content = document
		.querySelector(`meta[name="nwga:${name}"]`)
		?.getAttribute("content")
		?.trim();

	// The server writes the tag unconditionally and leaves it empty when it has
	// nothing to say, so "present but blank" has to mean "not configured" rather
	// than "configured as an empty string".
	return content === undefined || content.length === 0 ? undefined : content;
}

const schema = z.object({
	VITE_API_URL: z.url().default("http://localhost:3001"),
	/** Optional: no Turnstile key means no challenge, which is the local default. */
	VITE_TURNSTILE_SITE_KEY: z.string().optional(),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
	throw new Error(
		`Invalid browser environment:\n${parsed.error.issues
			.map((issue) => `  • ${issue.path.join(".")}: ${issue.message}`)
			.join("\n")}`,
	);
}

const apiUrl = metaConfig("api-url") ?? parsed.data.VITE_API_URL;

export const env = {
	apiUrl: apiUrl.replace(/\/+$/, ""),
	/**
	 * Where this client is served from, for building callback URLs.
	 *
	 * Read from the browser rather than configured: the app is reachable at
	 * whatever origin the person actually typed, and a hard-coded value would
	 * send somebody on a preview deployment back to production. The server still
	 * validates every callback against `trustedOrigins`, so a hostile origin
	 * cannot turn this into an open redirect.
	 */
	webUrl: typeof window === "undefined" ? "" : window.location.origin,
	/**
	 * `undefined` disables the bot challenge in the UI entirely.
	 *
	 * It must agree with the API's `TURNSTILE_SECRET_KEY`: a key here with no
	 * secret there challenges people for nothing, and a secret there with no key
	 * here makes every sign-in fail with a missing token. `envRefinements` enforces
	 * the pair on the server; `bun run doctor` checks the two halves line up.
	 */
	turnstileSiteKey:
		metaConfig("turnstile-site-key") ?? parsed.data.VITE_TURNSTILE_SITE_KEY,
	isDevelopment: import.meta.env.DEV,
} as const;
